import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Checkbox,
  Code,
  Divider,
  Group,
  Modal,
  MultiSelect,
  NativeSelect,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useConsentTypes } from '../api/consent';
import { useQuestionSet, useQuestionSets } from '../api/question-sets';
import type {
  AnswerSpec,
  CollectImagesStep,
  CollectQuestionsStep,
  DecisionStep,
  HaltStep,
  ImageConfiguration,
  LoopControlStep,
  OutputFieldSpec,
  OutputStep,
  SetCaseStatusStep,
  WorkflowDefinitionGraph,
  WorkflowStep,
} from '../api/workflows';
import { useHaltReasonCodes } from '../api/workflows';
import {
  consentGateOptions,
  findLoopByControl,
  isLoopMember,
  moveStepOrder,
  outputSourceLoopWarning,
  questionRefs,
  removeSteps,
  renameStep,
  setAwaitCompletion,
  setConsentType,
  setLoopMaxIterations,
  setLoopMembership,
  setLoopPerFinding,
  setLoopStart,
  setTransition,
  stepsByOrder,
  updateStep,
} from './workflow-edit-helpers';

interface Props {
  definition: WorkflowDefinitionGraph;
  selectedId: string;
  setDefinition: (next: WorkflowDefinitionGraph) => void;
  onClearSelection: () => void;
  // Org + product the workflow is scoped to. Used to filter the
  // question-set picker for collect_questions steps (and the decision
  // picker, which resolves question ids by looking up the referenced
  // set). Templates have no owning tenant, so the picker shows an
  // empty state in that case.
  workflowOrgId: string | null;
  workflowProductId: string | null;
}

const ID_RE = /^[a-z0-9_]+$/;

/**
 * The side panel exposes per-kind authoring controls. All edits flow
 * through `setDefinition` immediately (no batched-save, matching the
 * Figma/Notion model the design call settled on); the page-level
 * dirty indicator picks them up. The panel never owns the source of
 * truth — every input is a controlled value derived from the latest
 * definition prop.
 */
export function WorkflowEditSidePanel({
  definition,
  selectedId,
  setDefinition,
  onClearSelection,
  workflowOrgId,
  workflowProductId,
}: Props): JSX.Element {
  const step = definition.steps[selectedId];
  const [renameDraft, setRenameDraft] = useState<string | null>(null);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [confirmDelete, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  // Resolved question sets for this workflow's (org, product) tuple.
  // Used by both the collect_questions picker (filters active sets the
  // operator can pick from) and the decision picker (resolves question
  // ids inside referenced sets so the dropdown can list them). Keyed
  // off productId only — default workflows (orgId=null, productId set)
  // still need the map populated so the decision form can resolve refs.
  const allQuestionSetsForWorkflow = useQuestionSets(
    workflowProductId
      ? {
          ...(workflowOrgId ? { orgId: workflowOrgId } : {}),
          productId: workflowProductId,
        }
      : {},
  );
  const questionsByQuestionSetId = new Map(
    (allQuestionSetsForWorkflow.data?.items ?? []).map((qs) => [qs.id, qs.questions]),
  );

  // Inbound transition count, surfaced in the delete modal so the
  // operator knows what edges will be pruned as a side effect.
  const inboundTransitionCount = Object.values(definition.steps).reduce((acc, s) => {
    if (!s.transitions) return acc;
    return acc + Object.values(s.transitions).filter((target) => target === selectedId).length;
  }, 0);

  function handleDeleteStep() {
    setDefinition(removeSteps(definition, new Set([selectedId])));
    closeDeleteModal();
    onClearSelection();
  }

  if (!step) {
    return (
      <Stack p="md" gap="xs">
        <Text c="dimmed" size="sm">
          The selected step <Code>{selectedId}</Code> no longer exists in the graph.
        </Text>
        <Button variant="default" size="xs" onClick={onClearSelection}>
          Clear selection
        </Button>
      </Stack>
    );
  }

  // Step rename. Caller side-effect-free until `Apply` so the operator
  // can type freely without trampling the canvas. Validation: alpha-
  // numeric+underscore (matches the orchestrator's id regex) + no
  // collisions.
  function handleApplyRename() {
    if (renameDraft === null) return;
    if (renameDraft === selectedId) {
      setRenameDraft(null);
      setRenameError(null);
      return;
    }
    if (!ID_RE.test(renameDraft)) {
      setRenameError('IDs must be alphanumeric or underscore.');
      return;
    }
    if (definition.steps[renameDraft]) {
      setRenameError(`Step "${renameDraft}" already exists.`);
      return;
    }
    setDefinition(renameStep(definition, selectedId, renameDraft));
    setRenameDraft(null);
    setRenameError(null);
  }

  function handleStepUpdate(next: WorkflowStep) {
    setDefinition(updateStep(definition, selectedId, next));
  }

  return (
    <Stack p="md" gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Text size="xs" c="dimmed" tt="uppercase">
            Step
          </Text>
          <Text fw={600}>{selectedId}</Text>
          <Text size="xs" c="dimmed">
            kind: <Code>{step.kind}</Code>
          </Text>
        </div>
        <Button variant="subtle" size="xs" onClick={onClearSelection}>
          Close
        </Button>
      </Group>

      <Divider label="Identifier" labelPosition="left" />
      <Group gap="xs" align="flex-end">
        <TextInput
          label="Step id"
          description="Letters, digits, underscore. Used in transitions + decisions."
          value={renameDraft ?? selectedId}
          onChange={(e) => {
            setRenameDraft(e.currentTarget.value);
            setRenameError(null);
          }}
          error={renameError}
          style={{ flex: 1 }}
        />
        <Button
          variant="default"
          disabled={renameDraft === null || renameDraft === selectedId}
          onClick={handleApplyRename}
        >
          Apply
        </Button>
      </Group>

      <Divider label="Order" labelPosition="left" />
      <OrderControl definition={definition} selectedId={selectedId} setDefinition={setDefinition} />

      <Divider label="Start step" labelPosition="left" />
      {definition.start_step === selectedId ? (
        <Text size="sm" c="dimmed">
          This is the workflow&apos;s start step — execution begins here.
        </Text>
      ) : (
        <Group gap="sm" align="center">
          <Text size="sm" c="dimmed">
            Currently <Code>{definition.start_step}</Code>.
          </Text>
          <Button
            variant="default"
            size="xs"
            onClick={() => setDefinition({ ...definition, start_step: selectedId })}
          >
            Make this the start step
          </Button>
        </Group>
      )}

      <Divider label="Configuration" labelPosition="left" />
      {step.kind === 'collect_questions' && (
        <CollectQuestionsForm
          step={step as CollectQuestionsStep}
          onChange={handleStepUpdate}
          workflowOrgId={workflowOrgId}
          workflowProductId={workflowProductId}
        />
      )}
      {step.kind === 'collect_images' && (
        <CollectImagesForm step={step as CollectImagesStep} onChange={handleStepUpdate} />
      )}
      {step.kind === 'decision' && (
        <DecisionForm
          step={step as DecisionStep}
          onChange={handleStepUpdate}
          questionOptions={questionRefs(definition, questionsByQuestionSetId).filter(
            (r) => r.stepId !== selectedId,
          )}
        />
      )}
      {step.kind === 'output' && (
        <OutputForm step={step as OutputStep} onChange={handleStepUpdate} definition={definition} />
      )}
      {step.kind === 'run_ai_review' && (
        <RunAiReviewForm
          definition={definition}
          selectedId={selectedId}
          setDefinition={setDefinition}
        />
      )}
      {step.kind === 'halt' && (
        <HaltForm step={step as HaltStep} onChange={handleStepUpdate} orgId={workflowOrgId} />
      )}
      {step.kind === 'set_case_status' && (
        <SetCaseStatusForm step={step as SetCaseStatusStep} onChange={handleStepUpdate} />
      )}
      {step.kind === 'gate_on_consent' && (
        <GateOnConsentForm
          definition={definition}
          selectedId={selectedId}
          setDefinition={setDefinition}
          workflowOrgId={workflowOrgId}
          workflowProductId={workflowProductId}
        />
      )}
      {step.kind === 'loop_control' && (
        <LoopControlForm
          definition={definition}
          selectedId={selectedId}
          setDefinition={setDefinition}
        />
      )}
      {hasLinearTransition(step.kind) && (
        <NextStepPicker definition={definition} stepId={selectedId} setDefinition={setDefinition} />
      )}
      {!isAuthorableKind(step.kind) && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          The <Code>{step.kind}</Code> kind is server-driven; per-node config isn&apos;t authored
          here. Edit the underlying definition JSON via the orchestrator API if you need to change
          it.
        </Alert>
      )}

      <Divider label="Danger zone" labelPosition="left" color="red" />
      <Button
        color="red"
        variant="subtle"
        leftSection={<IconTrash size={14} />}
        onClick={openDeleteModal}
      >
        Delete step
      </Button>

      <Modal
        opened={confirmDelete}
        onClose={closeDeleteModal}
        title={`Delete step "${selectedId}"`}
        centered
        transitionProps={{ duration: 0 }}
      >
        <Stack>
          <Text size="sm">
            Remove <Code>{selectedId}</Code> from this workflow?
          </Text>
          {inboundTransitionCount > 0 && (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
              {inboundTransitionCount} inbound transition
              {inboundTransitionCount === 1 ? '' : 's'} will also be removed. You may need to
              reconnect upstream steps after delete.
            </Alert>
          )}
          {definition.start_step === selectedId && (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
              This is the workflow&apos;s start step. After delete, the start_step pointer will fall
              back to the first remaining step — pick the intended new start before publish.
            </Alert>
          )}
          <Text size="xs" c="dimmed">
            Save the draft to persist this change. The workflow won&apos;t be republished until you
            click Publish.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDeleteStep}>
              Delete step
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function isAuthorableKind(kind: string): boolean {
  return [
    'collect_questions',
    'collect_images',
    'decision',
    'output',
    'run_ai_review',
    'gate_on_consent',
    'halt',
    'set_case_status',
    'loop_control',
  ].includes(kind);
}

// Linear-flow kinds run, then fall through to a single next step via
// `on_complete`. Decision uses its own branch model and output is a
// terminal — neither needs the "Next step" picker.
function hasLinearTransition(kind: string): boolean {
  return ['collect_questions', 'collect_images', 'run_ai_review', 'set_case_status'].includes(kind);
}

/**
 * Inline "Next step" picker for linear-flow kinds. Writes the
 * `on_complete` transition so the orchestrator's reachability validator
 * sees a path from this step to whatever the operator picks. Cheaper
 * than asking operators to learn React Flow's edge-drag affordance,
 * and it's discoverable inside the side panel they're already in.
 */
function NextStepPicker({
  definition,
  stepId,
  setDefinition,
}: {
  definition: WorkflowDefinitionGraph;
  stepId: string;
  setDefinition: (next: WorkflowDefinitionGraph) => void;
}) {
  const step = definition.steps[stepId];
  const current = step.transitions?.on_complete ?? '';
  const others = Object.keys(definition.steps).filter((id) => id !== stepId);
  return (
    <NativeSelect
      label="Next step (on_complete)"
      description="Where execution goes after this step finishes. Required for the runtime to reach a terminal output."
      value={current}
      onChange={(e) => {
        const target = e.currentTarget.value;
        const rest = { ...(step.transitions ?? {}) };
        if (target) {
          rest.on_complete = target;
        } else {
          delete rest.on_complete;
        }
        const nextStep = {
          ...step,
          transitions: Object.keys(rest).length ? rest : undefined,
        } as WorkflowStep;
        setDefinition({ ...definition, steps: { ...definition.steps, [stepId]: nextStep } });
      }}
      data={[
        { value: '', label: 'Select a next step…' },
        ...others.map((id) => ({
          value: id,
          label: `${id} (${definition.steps[id].kind})`,
        })),
      ]}
    />
  );
}

/**
 * Authoring panel for a `gate_on_consent` step. The operator picks which
 * configured consent the gate checks, plus the two branch targets on_pass
 * (granted) / on_fail (missing consent).
 */
function GateOnConsentForm({
  definition,
  selectedId,
  setDefinition,
  workflowOrgId,
  workflowProductId,
}: {
  definition: WorkflowDefinitionGraph;
  selectedId: string;
  setDefinition: (next: WorkflowDefinitionGraph) => void;
  workflowOrgId: string | null;
  workflowProductId: string | null;
}) {
  const step = definition.steps[selectedId];
  const consents = useConsentTypes(workflowProductId ?? undefined);
  const options = consentGateOptions(consents.data ?? [], workflowOrgId);
  const selectedCode = (step.params as { types?: string[] } | undefined)?.types?.[0] ?? '';
  const consentsLink = workflowOrgId
    ? `/orgs/${workflowOrgId}/products/${workflowProductId}/consent`
    : `/products/${workflowProductId}/consent`;

  const others = Object.keys(definition.steps).filter((id) => id !== selectedId);
  const targetData = [
    { value: '', label: 'Select a step…' },
    ...others.map((id) => ({ value: id, label: `${id} (${definition.steps[id].kind})` })),
  ];

  return (
    <Stack gap="sm">
      {!workflowProductId ? (
        <Alert color="blue" icon={<IconAlertTriangle size={16} />}>
          This workflow isn&apos;t bound to a product, so specific consents can&apos;t be listed. At
          runtime the gate checks all blocking consents configured for the case&apos;s
          product/organisation.
        </Alert>
      ) : consents.isPending ? (
        <Text c="dimmed" size="sm">
          Loading consents…
        </Text>
      ) : options.length === 0 ? (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">
            No consents are configured for this product. Add them on the{' '}
            <Anchor component={Link} to={consentsLink} size="sm">
              Consents
            </Anchor>{' '}
            page, then come back to pick one.
          </Text>
        </Alert>
      ) : (
        <NativeSelect
          label="Consent to check"
          description="Which configured consent must be granted to pass."
          value={selectedCode}
          onChange={(e) =>
            setDefinition(setConsentType(definition, selectedId, e.currentTarget.value))
          }
          data={[
            { value: '', label: 'Select a consent…' },
            ...options.map((o) => ({ value: o.code, label: o.display_name })),
          ]}
        />
      )}
      <NativeSelect
        label="On granted (on_pass)"
        description="Where execution continues when the required consents are granted."
        value={step.transitions?.on_pass ?? ''}
        onChange={(e) =>
          setDefinition(setTransition(definition, selectedId, 'on_pass', e.currentTarget.value))
        }
        data={targetData}
      />
      <NativeSelect
        label="On missing consent (on_fail)"
        description="Where execution goes when a required consent is not granted (e.g. a terminal output)."
        value={step.transitions?.on_fail ?? ''}
        onChange={(e) =>
          setDefinition(setTransition(definition, selectedId, 'on_fail', e.currentTarget.value))
        }
        data={targetData}
      />
    </Stack>
  );
}

/**
 * Authoring panel for a `run_ai_review` step: the `on_failure` branch
 * target (required by the orchestrator validator for a `per_finding`
 * loop member — see definition-validator.ts — but allowed to be set on
 * any run_ai_review step) and the `await_completion` dispatch mode
 * (#1292/#1297's non-blocking fire-and-forget option). `on_complete` is
 * handled separately by the file's existing `hasLinearTransition` /
 * `NextStepPicker` wiring, not duplicated here.
 */
export function RunAiReviewForm({
  definition,
  selectedId,
  setDefinition,
}: {
  definition: WorkflowDefinitionGraph;
  selectedId: string;
  setDefinition: (next: WorkflowDefinitionGraph) => void;
}) {
  const step = definition.steps[selectedId];
  const others = Object.keys(definition.steps).filter((id) => id !== selectedId);
  const targetData = [
    { value: '', label: 'Select a step…' },
    ...others.map((id) => ({ value: id, label: `${id} (${definition.steps[id].kind})` })),
  ];
  const awaitCompletion =
    (step.params as { await_completion?: boolean } | undefined)?.await_completion !== false;

  return (
    <Stack gap="sm">
      <NativeSelect
        label="On failure (on_failure)"
        description="Where execution goes if the AI review fails. Leaving this blank on any run_ai_review step risks an unexplained halt (on_failure_transition_missing) if the review fails at runtime — not just inside a per_finding loop, where the orchestrator also enforces it at publish time."
        value={step.transitions?.on_failure ?? ''}
        onChange={(e) =>
          setDefinition(setTransition(definition, selectedId, 'on_failure', e.currentTarget.value))
        }
        data={targetData}
      />
      <Checkbox
        label="Wait for AI review to complete"
        description="Unchecked dispatches the review and moves on immediately (params.await_completion: false); the result attaches later via the per-finding results path."
        checked={awaitCompletion}
        onChange={(e) =>
          setDefinition(setAwaitCompletion(definition, selectedId, e.currentTarget.checked))
        }
      />
    </Stack>
  );
}

/**
 * Authoring panel for a `loop_control` step and its owning loop entry.
 * Unlike every other per-kind form, this one edits both the step
 * (its `done` transition) and a sibling `loops[loopId]` entry —
 * membership, start, and max_iterations all live there, not on the
 * step itself. `repeat` is intentionally not a picker: it's a fixed
 * invariant (`control.transitions.repeat === loop.start`), kept in
 * sync by `setLoopStart` whenever the operator changes `start`.
 */
export function LoopControlForm({
  definition,
  selectedId,
  setDefinition,
}: {
  definition: WorkflowDefinitionGraph;
  selectedId: string;
  setDefinition: (next: WorkflowDefinitionGraph) => void;
}) {
  const step = definition.steps[selectedId] as LoopControlStep;
  const found = findLoopByControl(definition, selectedId);

  if (!found) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />}>
        No loop is registered for this control step. This shouldn&apos;t happen — dropping a Loop
        control node always creates its owning loop. Delete and re-add this step.
      </Alert>
    );
  }
  const [loopId, loop] = found;

  // Candidates: every other step not already claimed by a *different*
  // loop (v1: no shared membership, mirrors the backend validator).
  // The control step itself is never a member of its own loop.
  const membershipCandidates = Object.keys(definition.steps).filter(
    (id) => id !== selectedId && (!isLoopMember(definition, id) || loop.steps.includes(id)),
  );
  const membershipData = membershipCandidates.map((id) => ({
    value: id,
    label: `${id} (${definition.steps[id].kind})`,
  }));

  const doneTargets = Object.keys(definition.steps).filter(
    (id) => id !== selectedId && !loop.steps.includes(id),
  );
  const doneData = [
    { value: '', label: 'Select a step…' },
    { value: 'TERMINAL', label: 'TERMINAL (end the workflow)' },
    // Labelled distinctly from `membershipData`'s "id (kind)" format —
    // before any members are assigned the two lists contain the same
    // step ids, and an identical label would give a native <option> and
    // the open MultiSelect's combobox option the same accessible name.
    ...doneTargets.map((id) => ({ value: id, label: `${id} — ${definition.steps[id].kind}` })),
  ];

  return (
    <Stack gap="sm">
      <MultiSelect
        label="Loop members"
        description="Steps that repeat each pass, in addition to this control step (always a member). Include the start step and every step reachable within one pass."
        value={loop.steps.filter((id) => id !== selectedId)}
        onChange={(ids) =>
          setDefinition(setLoopMembership(definition, loopId, [...ids, selectedId]))
        }
        data={membershipData}
      />
      <NativeSelect
        label="Start step"
        description="First member step entered each pass."
        value={loop.start}
        onChange={(e) => setDefinition(setLoopStart(definition, loopId, e.currentTarget.value))}
        data={[
          { value: '', label: 'Select a start step…' },
          ...loop.steps
            .filter((id) => id !== selectedId)
            .map((id) => ({ value: id, label: `${id} (${definition.steps[id].kind})` })),
        ]}
      />
      <Text size="sm" c={loop.start ? undefined : 'dimmed'}>
        {/* Kept as a single flat text node (no nested <Code>/<span>) so the
            whole line — including the resolved start step id — reads as
            one readable sentence rather than a picker. */}
        <Text span fw={500}>
          {loop.start
            ? `repeat: → ${loop.start} (fixed to the start step above)`
            : 'repeat: (set a start step first)'}
        </Text>
      </Text>
      <NativeSelect
        label="On done"
        description="Where execution goes once the client signals it's finished repeating."
        value={step.transitions?.done ?? ''}
        onChange={(e) =>
          setDefinition(setTransition(definition, selectedId, 'done', e.currentTarget.value))
        }
        data={doneData}
      />
      <NumberInput
        label="Max iterations (optional)"
        description="Runtime default is 3 if left blank."
        min={1}
        value={loop.max_iterations ?? ''}
        onChange={(v) =>
          setDefinition(
            setLoopMaxIterations(definition, loopId, typeof v === 'number' ? v : undefined),
          )
        }
      />
      <Checkbox
        label="One lesion per pass"
        description="Each repeat of this loop is tied to a single lesion. The client must identify which lesion it's reporting on before adding another pass or finishing."
        checked={loop.per_finding ?? false}
        onChange={(e) =>
          setDefinition(setLoopPerFinding(definition, loopId, e.currentTarget.checked))
        }
      />
    </Stack>
  );
}

function OrderControl({
  definition,
  selectedId,
  setDefinition,
}: {
  definition: WorkflowDefinitionGraph;
  selectedId: string;
  setDefinition: (next: WorkflowDefinitionGraph) => void;
}) {
  const ordered = stepsByOrder(definition);
  const position = ordered.indexOf(selectedId);
  const total = ordered.length;
  const canMoveUp = position > 0;
  const canMoveDown = position >= 0 && position < total - 1;
  return (
    <Group gap="xs" align="center">
      <Text size="sm">
        Position{' '}
        <Code>
          {position + 1} / {total}
        </Code>
      </Text>
      <ActionIcon
        variant="default"
        disabled={!canMoveUp}
        onClick={() => setDefinition(moveStepOrder(definition, selectedId, 'up'))}
        aria-label="Move up"
      >
        <IconArrowUp size={14} />
      </ActionIcon>
      <ActionIcon
        variant="default"
        disabled={!canMoveDown}
        onClick={() => setDefinition(moveStepOrder(definition, selectedId, 'down'))}
        aria-label="Move down"
      >
        <IconArrowDown size={14} />
      </ActionIcon>
      <Text size="xs" c="dimmed">
        Display-only. Doesn&apos;t change execution flow.
      </Text>
    </Group>
  );
}

// ─── Per-kind forms ─────────────────────────────────────────────────────────

function CollectQuestionsForm({
  step,
  onChange,
  workflowOrgId,
  workflowProductId,
}: {
  step: CollectQuestionsStep;
  onChange: (next: CollectQuestionsStep) => void;
  workflowOrgId: string | null;
  workflowProductId: string | null;
}) {
  // Picker is usable whenever the workflow has a product. Org-scoped
  // workflows (orgId set + productId set) see org-specific + product
  // defaults; default workflows (orgId=null + productId set) see
  // product defaults only — an org-specific set on a workflow that
  // applies to every org would be semantically wrong. Pure templates
  // (no productId) still can't author.
  const isDefaultScope = !workflowOrgId && !!workflowProductId;
  const isOrgScope = !!workflowOrgId && !!workflowProductId;
  const hasScope = isOrgScope || isDefaultScope;
  const questionSets = useQuestionSets(
    hasScope
      ? {
          ...(isOrgScope ? { orgId: workflowOrgId as string } : {}),
          productId: workflowProductId as string,
          status: 'active',
        }
      : {},
  );
  const rawItems = hasScope ? (questionSets.data?.items ?? []) : [];
  const items = isDefaultScope ? rawItems.filter((qs) => qs.orgId === null) : rawItems;
  const selectedId = step.params.question_set_id;
  const selected = items.find((qs) => qs.id === selectedId);
  // No-scope branch falls back to a single-id fetch so a pre-existing
  // reference can still be displayed even though there's no list to
  // pick from.
  const selectedDirect = useQuestionSet(!hasScope && selectedId ? selectedId : undefined);

  if (!hasScope) {
    const set = selectedDirect.data;
    return (
      <Stack gap="sm">
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          Template workflows can&apos;t author collect_questions steps yet — clone this template
          into a tenant + product first, then pick a question set there.
        </Alert>
        {selectedId ? (
          <Text size="sm">
            <Text span fw={500}>
              Selected question set:{' '}
            </Text>
            {set ? (
              <Anchor component={Link} to={`/question-sets/${set.id}/edit`} size="sm">
                {set.name} (v{set.version}, {set.questions.length} question
                {set.questions.length === 1 ? '' : 's'})
              </Anchor>
            ) : selectedDirect.isPending ? (
              <Text span c="dimmed">
                loading…
              </Text>
            ) : (
              <Code>{selectedId}</Code>
            )}
          </Text>
        ) : (
          <Text size="sm" c="dimmed">
            No question set referenced yet.
          </Text>
        )}
      </Stack>
    );
  }

  if (questionSets.isPending) {
    return (
      <Text c="dimmed" size="sm">
        Loading question sets…
      </Text>
    );
  }

  if (items.length === 0) {
    return (
      <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
        <Stack gap="xs">
          <Text size="sm">
            {isDefaultScope ? (
              <>
                No active <strong>default</strong> question sets configured for this product. Create
                one in the{' '}
              </>
            ) : (
              <>
                No active question sets configured for this organisation + product. Create one in
                the{' '}
              </>
            )}
            <Anchor component={Link} to="/question-sets" size="sm">
              Question sets
            </Anchor>{' '}
            page (mark it as <Code>active</Code>
            {isDefaultScope ? ', and tick "Default for all organisations"' : ''}) and come back here
            to pick it.
          </Text>
        </Stack>
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <NativeSelect
        label="Question set"
        description={
          isDefaultScope
            ? 'Pick a product-wide default set. Org-specific sets are hidden here so a default workflow only references sets that apply to every org.'
            : 'Pick the published set whose questions this step should ask.'
        }
        required
        value={selectedId ?? ''}
        onChange={(e) => {
          onChange({ ...step, params: { question_set_id: e.currentTarget.value } });
        }}
        data={[
          { value: '', label: 'Select a question set…' },
          ...items.map((qs) => ({
            value: qs.id,
            label: `${qs.name} (v${qs.version}, ${qs.questions.length} question${
              qs.questions.length === 1 ? '' : 's'
            })${qs.orgId === null ? ' · default' : ''}`,
          })),
        ]}
      />
      {selected && (
        <Stack
          gap={2}
          p="sm"
          style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 4 }}
        >
          <Text size="xs" c="dimmed">
            Preview — questions will be snapshotted into each running instance when the step is
            reached.
          </Text>
          {selected.questions.map((q) => (
            <Text key={q.id} size="sm">
              <Code>{q.id}</Code> · {typeof q.prompt === 'string' ? q.prompt : q.prompt.default}{' '}
              <Text component="span" size="xs" c="dimmed">
                ({q.answer.type})
              </Text>
            </Text>
          ))}
          <Anchor component={Link} to={`/question-sets/${selected.id}`} size="xs">
            Edit this question set →
          </Anchor>
        </Stack>
      )}
    </Stack>
  );
}

// Friendly labels + helper text for each image configuration. The
// underlying wire values (`derm_only`, etc.) match the orchestrator's
// Zod enum and the runtime validator's expected payload shapes; only
// the operator-facing UI uses these prettier names. The helper text
// states the image-count rules the runtime enforces — `derm_only` is
// always exactly one dermoscopic, no macroscopic, no count to set.
const IMAGE_CONFIG_LABELS: Record<
  ImageConfiguration,
  { label: string; helper: string; hasMacro: boolean; hasDerm: boolean }
> = {
  derm_only: {
    label: 'Dermoscopic only',
    helper: 'Dermoscopic image(s), no macroscopic.',
    hasMacro: false,
    hasDerm: true,
  },
  derm_plus_macro: {
    label: 'Dermoscopic + macroscopic',
    helper: 'Both types per the counts below.',
    hasMacro: true,
    hasDerm: true,
  },
  macro_only: {
    label: 'Macroscopic only',
    helper: 'Macroscopic only, no dermoscopic.',
    hasMacro: true,
    hasDerm: false,
  },
};

function CollectImagesForm({
  step,
  onChange,
}: {
  step: CollectImagesStep;
  onChange: (next: CollectImagesStep) => void;
}) {
  const configurations = step.params.configurations ?? [];

  function toggle(c: ImageConfiguration) {
    const next = configurations.includes(c)
      ? configurations.filter((x) => x !== c)
      : [...configurations, c];
    onChange({ ...step, params: { ...step.params, configurations: next } });
  }

  const showDermoscopicRange = configurations.some((c) => IMAGE_CONFIG_LABELS[c]?.hasDerm);
  const showMacroscopicRange = configurations.some((c) => IMAGE_CONFIG_LABELS[c]?.hasMacro);

  return (
    <Stack gap="sm">
      <Text size="sm" fw={500}>
        Acceptable image patterns (any of)
      </Text>
      <Stack gap={4}>
        {(['derm_only', 'derm_plus_macro', 'macro_only'] as const).map((c) => (
          <Checkbox
            key={c}
            label={IMAGE_CONFIG_LABELS[c].label}
            description={IMAGE_CONFIG_LABELS[c].helper}
            checked={configurations.includes(c)}
            onChange={() => toggle(c)}
          />
        ))}
      </Stack>
      {showDermoscopicRange && (
        <ImageTypeRangeEditor
          step={step}
          onChange={onChange}
          type="dermoscopic"
          floor={1}
          label="Dermoscopic count range"
          optionalHelper="Client may submit zero dermoscopic images; if any are provided, the count must fall in [min, max]."
        />
      )}
      {showMacroscopicRange && (
        <ImageTypeRangeEditor
          step={step}
          onChange={onChange}
          type="macroscopic"
          floor={2}
          label="Macroscopic count range"
          optionalHelper="Client may submit zero macroscopic images; if any are provided, the count must fall in [min, max] — max stays ≥ 2 (clinical pair-shot floor)."
        />
      )}
    </Stack>
  );
}

/**
 * Shared editor for the per-image-type count + optionality block. The
 * dermoscopic and macroscopic variants only differ by the field name
 * they write to (`dermoscopic_count` vs `macroscopic_count`) and the
 * lower NumberInput floor (1 for derms, 2 for macros).
 */
function ImageTypeRangeEditor({
  step,
  onChange,
  type,
  floor,
  label,
  optionalHelper,
}: {
  step: CollectImagesStep;
  onChange: (next: CollectImagesStep) => void;
  type: 'dermoscopic' | 'macroscopic';
  floor: number;
  label: string;
  optionalHelper: string;
}) {
  const field = `${type}_count` as 'dermoscopic_count' | 'macroscopic_count';
  const range = step.params[field];
  const optional = range?.optional ?? false;

  function writeRange(next: { min: number; max: number; optional?: boolean } | undefined) {
    const rest = { ...step.params };
    if (next === undefined) {
      delete rest[field];
    } else {
      rest[field] = next;
    }
    onChange({ ...step, params: rest });
  }

  // Surface legacy values that pre-date the current schema floor (e.g.
  // workflows saved when macroscopic min could be 0) so the operator
  // can see why a save is being rejected.
  const belowFloor = range !== undefined && (range.min < floor || range.max < floor);

  return (
    <>
      <Divider />
      <Group justify="space-between" align="center">
        <Text size="sm" fw={500}>
          {label}
        </Text>
        {range && (
          <Anchor component="button" type="button" size="xs" onClick={() => writeRange(undefined)}>
            Clear constraints
          </Anchor>
        )}
      </Group>
      {belowFloor && (
        <Alert color="yellow" icon={<IconAlertTriangle size={14} />} p="xs">
          Saved value (min={range.min}, max={range.max}) is below the schema floor of {floor}. Bump
          the bound or click <Code>Clear constraints</Code> to leave the count unbounded.
        </Alert>
      )}
      <Checkbox
        label={`${type === 'dermoscopic' ? 'Dermoscopic' : 'Macroscopic'} images are optional`}
        description={optionalHelper}
        checked={optional}
        onChange={(e) => {
          const checked = e.currentTarget.checked;
          if (!range && checked) {
            writeRange({ min: floor, max: floor, optional: true });
            return;
          }
          if (!range) return;
          writeRange({ ...range, optional: checked || undefined });
        }}
      />
      <Group grow>
        <NumberInput
          label="Min"
          min={floor}
          value={range?.min ?? ''}
          onChange={(v) => {
            const min = typeof v === 'number' ? v : undefined;
            if (min === undefined) {
              // Clearing min when nothing else is set drops the whole block.
              if (!range || (range.max === undefined && !range.optional)) {
                writeRange(undefined);
                return;
              }
              return;
            }
            writeRange({
              min,
              max: range?.max ?? min,
              ...(range?.optional ? { optional: true } : {}),
            });
          }}
        />
        <NumberInput
          label="Max"
          min={floor}
          value={range?.max ?? ''}
          onChange={(v) => {
            const max = typeof v === 'number' ? v : undefined;
            if (max === undefined || range === undefined) return;
            writeRange({
              min: range.min,
              max,
              ...(range.optional ? { optional: true } : {}),
            });
          }}
        />
      </Group>
    </>
  );
}

export function DecisionForm({
  step,
  onChange,
  questionOptions,
}: {
  step: DecisionStep;
  onChange: (next: DecisionStep) => void;
  questionOptions: Array<{
    stepId: string;
    questionId: string;
    prompt: string;
    answer?: AnswerSpec;
  }>;
}) {
  const input = step.params.input;
  const branches = step.params.branches ?? [];
  const inputKey =
    input.step_id && input.question_id ? `${input.step_id}.${input.question_id}` : '';

  const selectedAnswer = questionOptions.find(
    (q) => q.stepId === input.step_id && q.questionId === input.question_id,
  )?.answer;

  function defaultWhenFor(answer: AnswerSpec | undefined): string | number | boolean | string[] {
    switch (answer?.type) {
      case 'boolean':
        return false;
      case 'numeric':
        return 0;
      case 'multi_choice':
        return [];
      default:
        // free_text, single_choice, unknown, or no question selected.
        return '';
    }
  }

  function setInputFromKey(key: string) {
    if (!key) {
      onChange({
        ...step,
        params: { ...step.params, input: { step_id: '', question_id: '' } },
      });
      return;
    }
    const dot = key.indexOf('.');
    const stepId = key.slice(0, dot);
    const questionId = key.slice(dot + 1);
    const nextAnswer = questionOptions.find(
      (q) => q.stepId === stepId && q.questionId === questionId,
    )?.answer;
    const resetWhen = defaultWhenFor(nextAnswer);
    onChange({
      ...step,
      params: {
        ...step.params,
        input: { step_id: stepId, question_id: questionId },
        // Stale `when` values authored against the previous question's
        // type would silently never match — reset each to the new
        // type's default so the operator re-enters via the typed control.
        branches: branches.map((b) => ({ ...b, when: resetWhen })),
      },
    });
  }

  function patchBranch(
    i: number,
    field: 'when' | 'outcome',
    val: string | number | boolean | string[],
  ) {
    const copy = [...branches];
    copy[i] = { ...copy[i], [field]: val };
    onChange({ ...step, params: { ...step.params, branches: copy } });
  }
  function removeBranch(i: number) {
    const copy = [...branches];
    copy.splice(i, 1);
    onChange({ ...step, params: { ...step.params, branches: copy } });
  }
  function addBranch() {
    onChange({
      ...step,
      params: {
        ...step.params,
        branches: [
          ...branches,
          { when: defaultWhenFor(selectedAnswer), outcome: `branch_${branches.length + 1}` },
        ],
      },
    });
  }

  return (
    <Stack gap="sm">
      <NativeSelect
        label="Input question"
        description="Pick a question from any collect_questions step in the graph."
        value={inputKey}
        onChange={(e) => setInputFromKey(e.currentTarget.value)}
        data={[
          { value: '', label: '(none — required before publish)' },
          ...questionOptions.map((q) => ({
            value: `${q.stepId}.${q.questionId}`,
            label: `${q.stepId}.${q.questionId} — ${q.prompt}`,
          })),
        ]}
      />
      <Divider label="Branches" labelPosition="left" />
      {branches.length === 0 && (
        <Text c="dimmed" size="sm">
          No branches yet. Each branch maps a value to an outcome name; outcome names then drive the
          step&apos;s transitions on the canvas.
        </Text>
      )}
      {branches.map((b, i) => (
        <Group key={i} gap="xs" align="flex-end">
          <WhenInput
            answer={selectedAnswer}
            value={b.when}
            onChange={(val) => patchBranch(i, 'when', val)}
          />
          <TextInput
            label="outcome"
            value={b.outcome}
            onChange={(e) => patchBranch(i, 'outcome', e.currentTarget.value)}
            size="xs"
            style={{ flex: 1 }}
          />
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={() => removeBranch(i)}
            aria-label="Remove branch"
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      ))}
      <Button size="xs" variant="light" leftSection={<IconPlus size={12} />} onClick={addBranch}>
        Add branch
      </Button>
      <TextInput
        label="Default outcome (optional)"
        description="Used when no branch's `when` matches the input value."
        value={step.params.default_outcome ?? ''}
        onChange={(e) =>
          onChange({
            ...step,
            params: { ...step.params, default_outcome: e.currentTarget.value || undefined },
          })
        }
      />
    </Stack>
  );
}

function WhenInput({
  answer,
  value,
  onChange,
}: {
  answer: AnswerSpec | undefined;
  value: string | number | boolean | string[];
  onChange: (next: string | number | boolean | string[]) => void;
}) {
  const label = 'when (value)';
  if (answer?.type === 'boolean') {
    return (
      <NativeSelect
        label={label}
        value={value === true ? 'true' : 'false'}
        onChange={(e) => onChange(e.currentTarget.value === 'true')}
        data={[
          { value: 'true', label: 'true' },
          { value: 'false', label: 'false' },
        ]}
        size="xs"
        style={{ flex: 1 }}
      />
    );
  }
  if (answer?.type === 'numeric') {
    return (
      <NumberInput
        label={label}
        value={typeof value === 'number' ? value : ''}
        onChange={(v) => onChange(typeof v === 'number' ? v : 0)}
        allowDecimal={answer.integer !== true}
        size="xs"
        style={{ flex: 1 }}
      />
    );
  }
  if (answer?.type === 'single_choice') {
    return (
      <NativeSelect
        label={label}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.currentTarget.value)}
        data={[
          { value: '', label: '(select)' },
          ...answer.options.map((o) => ({ value: o.value, label: o.value })),
        ]}
        size="xs"
        style={{ flex: 1 }}
      />
    );
  }
  if (answer?.type === 'multi_choice') {
    return (
      <MultiSelect
        label={label}
        value={Array.isArray(value) ? value : []}
        onChange={(v) => onChange(v)}
        data={answer.options.map((o) => ({ value: o.value, label: o.value }))}
        size="xs"
        style={{ flex: 1 }}
      />
    );
  }
  // free_text, unknown, or no question selected — keep the string input.
  return (
    <TextInput
      label={label}
      value={typeof value === 'string' ? value : String(value ?? '')}
      onChange={(e) => onChange(e.currentTarget.value)}
      size="xs"
      style={{ flex: 1 }}
    />
  );
}

export function OutputForm({
  step,
  onChange,
  definition,
}: {
  step: OutputStep;
  onChange: (next: OutputStep) => void;
  definition: WorkflowDefinitionGraph;
}) {
  const fields = step.params.fields ?? [];

  function patch(i: number, next: OutputFieldSpec) {
    const copy = [...fields];
    copy[i] = next;
    onChange({ ...step, params: { ...step.params, fields: copy } });
  }
  function remove(i: number) {
    const copy = [...fields];
    copy.splice(i, 1);
    onChange({ ...step, params: { ...step.params, fields: copy } });
  }
  function add() {
    onChange({
      ...step,
      params: {
        ...step.params,
        fields: [...fields, { name: `field_${fields.length + 1}`, type: 'string' }],
      },
    });
  }

  return (
    <Stack gap="sm">
      {fields.length === 0 && (
        <Text c="dimmed" size="sm">
          No fields yet.
        </Text>
      )}
      {fields.map((f, i) => {
        const loopWarning = outputSourceLoopWarning(definition, f.source);
        return (
          <Stack key={i} gap={2}>
            <Group gap="xs" align="flex-end">
              <TextInput
                label="name"
                value={f.name}
                onChange={(e) => patch(i, { ...f, name: e.currentTarget.value })}
                size="xs"
                style={{ flex: 1 }}
              />
              <NativeSelect
                label="type"
                value={f.type}
                onChange={(e) =>
                  patch(i, { ...f, type: e.currentTarget.value as OutputFieldSpec['type'] })
                }
                size="xs"
                data={[
                  { value: 'string', label: 'string' },
                  { value: 'number', label: 'number' },
                  { value: 'boolean', label: 'boolean' },
                  { value: 'string_array', label: 'string_array' },
                  { value: 'object', label: 'object' },
                ]}
              />
              <TextInput
                label="source (optional)"
                description="e.g. q1.has_lesion"
                value={f.source ?? ''}
                onChange={(e) => patch(i, { ...f, source: e.currentTarget.value || undefined })}
                size="xs"
                style={{ flex: 1.5 }}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => remove(i)}
                aria-label="Remove field"
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
            {loopWarning && (
              <Text size="xs" c="yellow.7">
                ⚠ &quot;{f.source}&quot; is inside loop &quot;{loopWarning}&quot; — this only
                captures the last iteration. Use &quot;{loopWarning}&quot; as the source instead to
                get every iteration.
              </Text>
            )}
          </Stack>
        );
      })}
      <Button size="xs" variant="light" leftSection={<IconPlus size={12} />} onClick={add}>
        Add field
      </Button>
    </Stack>
  );
}

export function HaltForm({
  step,
  onChange,
  orgId,
}: {
  step: HaltStep;
  onChange: (next: HaltStep) => void;
  orgId?: string | null;
}) {
  const { data: codes, isLoading, isError } = useHaltReasonCodes(orgId);
  const options = (codes ?? []).map((c) => ({ value: c.code, label: c.label }));
  return (
    <Stack gap="sm">
      {isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          Could not load reason codes. Check the network tab for the API error, or run the
          orchestrator seed (<code>pnpm --filter @sa-platform/orchestrator prisma:seed</code>).
        </Alert>
      )}
      <Select
        label="Reason code"
        description="Code surfaced to the client as the halt reason. Must match a registered reason code."
        data={options}
        value={step.params.reason_code ?? null}
        onChange={(v) => onChange({ ...step, params: { reason_code: v ?? undefined } })}
        disabled={isLoading || isError}
        placeholder={isLoading ? 'Loading…' : 'Select a reason code'}
        searchable
      />
    </Stack>
  );
}

export function SetCaseStatusForm({
  step,
  onChange,
}: {
  step: SetCaseStatusStep;
  onChange: (next: SetCaseStatusStep) => void;
}) {
  return (
    <Stack gap="sm">
      <NativeSelect
        label="Case status"
        description="The status to write on the case when this step executes. The clinical-api applies it immediately; on_complete fires once the write succeeds."
        value={step.params.status}
        onChange={(e) => onChange({ ...step, params: { status: e.currentTarget.value } })}
        data={[
          { value: '', label: 'Select a status…' },
          { value: 'open', label: 'Open' },
          { value: 'awaiting_histology', label: 'Awaiting histology' },
          { value: 'completed', label: 'Completed' },
        ]}
      />
    </Stack>
  );
}
